import { useSensorQueryV1 } from "@/api/v1/sensor-api";
import { useUpdateSensorQuery } from "@/api/v0/sensors";
import { getRouteApi } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyMedia,
} from "@/components/ui/empty";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Copy,
  Edit,
  X,
  ArrowLeft,
  Loader2,
  Antenna,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Activity,
  Settings,
  Info,
  Database,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useLatestDetections } from "@/hooks/use-latest-detections";
import { SensorLogsViewer } from "@/components/sensor-logs/sensor-logs-viewer";
import { SensorForm } from "@/components/sensors/sensor-form";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

export const SensorRoute = () => {
  const navigate = useNavigate();
  const sensorRoute = getRouteApi("/sensors/$sensorId");
  const params = sensorRoute.useParams();
  const { data: sensor, isPending, isError } = useSensorQueryV1(params.sensorId);
  const updateSensor = useUpdateSensorQuery();
  const [isEditing, setIsEditing] = useState(false);

  const detections = useLatestDetections().filter(
    (d) => d.sensor_id === params.sensorId
  );

  const handleCopyId = async () => {
    if (!sensor?.slug) return;
    try {
      await navigator.clipboard.writeText(sensor.slug);
      toast("Sensor slug copied to clipboard.");
    } catch {
      toast("Failed to copy sensor slug.");
    }
  };

  const handleEditSubmit = async (data: {
    name: string;
    latitude: number;
    longitude: number;
  }) => {
    if (!sensor) return;

    try {
      // Note: Update sensor mutation still uses v0 API
      // TODO: Implement v1 update sensor mutation
      await updateSensor.mutateAsync({
        sensorId: sensor.slug || sensor.organisation_id.toString(),
        name: data.name,
        latitude: data.latitude,
        longitude: data.longitude,
      });
      toast.success("Sensor updated successfully");
      setIsEditing(false);
    } catch {
      toast.error("Failed to update sensor");
    }
  };

  if (isPending) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Sensors", href: "/sensors" },
              { label: "Loading..." },
            ]}
            className="mb-6"
          />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading sensor data...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (isError || !sensor) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Sensors", href: "/sensors" },
              { label: "Error" },
            ]}
            className="mb-6"
          />
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load sensor data</p>
                <p>
                  The sensor you're looking for could not be found or there was
                  an error loading the data.
                </p>
                <Button
                  onClick={() => navigate({ to: "/sensors" })}
                  className="mt-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Sensors
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    );
  }

  const isOnline = sensor.status === "active" || sensor.status === "online";
  const lastSeen = sensor.updated_at
    ? new Date(sensor.updated_at).toLocaleString()
    : "never";

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Sensors", href: "/sensors" },
            { label: sensor.name },
          ]}
          className="mb-6"
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate({ to: "/sensors" })}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">
                {sensor.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Remote ID sensor monitoring and configuration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={isOnline ? "default" : "destructive"}
              className={
                isOnline
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : ""
              }
            >
              {isOnline ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Online
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Offline
                </>
              )}
            </Badge>
            {isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(false)}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={true}
                title="Update sensor mutation not yet implemented in v1"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Status
              </CardTitle>
              <Antenna className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div
                className={`text-lg sm:text-2xl font-bold ${isOnline ? "text-green-600" : "text-red-600"}`}
              >
                {isOnline ? "Online" : "Offline"}
              </div>
              <p className="text-xs text-muted-foreground">
                {isOnline ? "Currently active" : `Last seen ${lastSeen}`}
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Detections
              </CardTitle>
              <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {detections.length}
              </div>
              <p className="text-xs text-muted-foreground">Recent detections</p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Location
              </CardTitle>
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {sensor.latitude ? sensor.latitude.toFixed(4) : "N/A"}
              </div>
              <p className="text-xs text-muted-foreground">
                {sensor.latitude && sensor.longitude 
                  ? `Lat, ${sensor.longitude.toFixed(4)} Long`
                  : "Location not set"}
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">
                Created
              </CardTitle>
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
            </CardHeader>
            <CardContent className="pt-2 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">
                {new Date(sensor.created_at).toLocaleDateString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {new Date(sensor.created_at).toLocaleTimeString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        {isEditing ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Edit Sensor
              </CardTitle>
              <CardDescription>
                Update sensor configuration and location settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SensorForm
                initialValues={{
                  sensor_name: sensor.name,
                  latitude: sensor.latitude ?? 0,
                  longitude: sensor.longitude ?? 0,
                }}
                onSubmit={handleEditSubmit}
                isSubmitting={updateSensor.isPending}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
            {/* Overview and Logs */}
            <div className="space-y-4 sm:space-y-6">
              <Tabs defaultValue="overview">
                <TabsList className="grid w-full grid-cols-2 h-8 sm:h-10">
                  <TabsTrigger value="overview" className="text-xs sm:text-sm">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="logs" className="text-xs sm:text-sm">
                    Live Logs
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="overview"
                  className="space-y-4 sm:space-y-6"
                >
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        Sensor Information
                      </CardTitle>
                      <CardDescription>
                        Basic sensor details and configuration
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Sensor Slug</p>
                          <p className="text-base font-mono text-muted-foreground">
                            {sensor.slug || "N/A"}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyId}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <p className="text-sm font-medium">Organization ID</p>
                          <p className="text-base font-normal">
                            {sensor.organisation_id || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Status</p>
                          <Badge variant="secondary" className="mt-1">
                            {sensor.status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Access Mode</p>
                          <Badge variant="outline" className="mt-1">
                            {sensor.access_mode}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Location</p>
                          <p className="text-base font-normal">
                            {sensor.latitude && sensor.longitude
                              ? `Lat ${sensor.latitude.toFixed(6)}, Long ${sensor.longitude.toFixed(6)}`
                              : "Location not set"}
                          </p>
                        </div>
                        {sensor.description && (
                          <div>
                            <p className="text-sm font-medium">Description</p>
                            <p className="text-base font-normal">
                              {sensor.description}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium">Created At</p>
                          <p className="text-base font-normal">
                            {new Date(sensor.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Updated At</p>
                          <p className="text-base font-normal">
                            {new Date(sensor.updated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="logs" className="space-y-4 sm:space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Live Logs
                      </CardTitle>
                      <CardDescription>
                        Real-time sensor activity and detection logs
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SensorLogsViewer sensorId={params.sensorId} />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Raw Data */}
            <div className="space-y-4 sm:space-y-6">
              <Tabs defaultValue="raw">
                <TabsList className="grid w-full grid-cols-2 h-8 sm:h-10">
                  <TabsTrigger value="raw" className="text-xs sm:text-sm">
                    Raw Data
                  </TabsTrigger>
                  <TabsTrigger
                    value="raw-detections"
                    className="text-xs sm:text-sm"
                  >
                    Detections
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="raw" className="space-y-4 sm:space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Sensor Details (JSON)
                      </CardTitle>
                      <CardDescription>
                        Complete sensor configuration data
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px] rounded border bg-muted/50 p-3">
                        <pre className="text-sm whitespace-pre-wrap font-mono">
                          {JSON.stringify(sensor, null, 2)}
                        </pre>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent
                  value="raw-detections"
                  className="space-y-4 sm:space-y-6"
                >
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Sensor Detections (JSON)
                      </CardTitle>
                      <CardDescription>
                        Recent detection data from this sensor
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {detections.length > 0 ? (
                        <ScrollArea className="h-[400px] rounded border bg-muted/50 p-3">
                          <pre className="text-sm whitespace-pre-wrap font-mono">
                            {detections
                              .map((d) => JSON.stringify(d, null, 2))
                              .join("\n\n")}
                          </pre>
                        </ScrollArea>
                      ) : (
                        <Empty>
                          <EmptyMedia variant="icon">
                            <Activity className="h-6 w-6" />
                          </EmptyMedia>
                          <EmptyHeader>
                            <EmptyTitle>No detections</EmptyTitle>
                            <EmptyDescription>
                              No recent detections found for this sensor.
                            </EmptyDescription>
                          </EmptyHeader>
                        </Empty>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Sensor Management</p>
              <p>
                Monitor your sensor's status, view live detection logs, and
                manage configuration settings. Use the raw data tabs to inspect
                detailed sensor information and detection records for debugging
                and analysis.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
};
